/**
 * Lógica pura de las cuentas corrientes de los fiduciantes: saldo de gastos (aportado − su parte de los gastos
 * efectuados) y movimientos con saldo acumulado. Lo calcula todo el backend; acá sólo se decide cómo mostrarlo.
 * Montos como texto con punto decimal ("-1500.50").
 */
import type { BadgeTono, OpcionSelect } from "@/components";
import type { Cuenta, CuentaMia, Movimiento, TipoMovimiento, TotalesCuentas } from "./api";
import { formatFecha, formatMoneda } from "./format";
import { parseMonto, RUTA_COMPRA_TERRENO, TODOS } from "./gastos";
import { nombreMes } from "./honorarios";

export type EstadoSaldo = "a_favor" | "debe" | "al_dia";

const sinSigno = (monto: string): string => monto.trim().replace(/^-/, "");

/** > 0 a favor, < 0 debe, 0 al día. */
export function estadoSaldo(saldo: string): EstadoSaldo {
  if (parseMonto(sinSigno(saldo)) === null) return "al_dia";
  return saldo.trim().startsWith("-") ? "debe" : "a_favor";
}

export const ETIQUETA_SALDO: Record<EstadoSaldo, string> = { a_favor: "A favor", debe: "Debe", al_dia: "Al día" };
export const TONO_SALDO: Record<EstadoSaldo, BadgeTono> = { a_favor: "exito", debe: "peligro", al_dia: "neutro" };

/** El saldo sin signo, en USD (el signo lo dice la etiqueta: "A favor" o "Debe"). */
export const formatSaldo = (saldo: string): string => formatMoneda(sinSigno(saldo), "USD");

/** Color del texto de un saldo o de un movimiento: verde a favor, rojo debe. */
export const COLOR_SALDO: Record<EstadoSaldo, string> = { a_favor: "text-exito", debe: "text-peligro", al_dia: "text-texto-suave" };

/** El monto de un movimiento en una sola columna (Mati, 2026-11-02, en vez de Debe y Haber): lo que entra
 * "USD X" (a favor) y lo que sale "(USD X)" (debe), como el saldo; "—" si es cero. */
export function montoMovimiento(m: Movimiento): { texto: string; estado: EstadoSaldo } {
  if (parseMonto(m.haber_usd) !== null) return { texto: formatMoneda(m.haber_usd, "USD"), estado: "a_favor" };
  if (parseMonto(m.debe_usd) !== null) return { texto: `(${formatMoneda(m.debe_usd, "USD")})`, estado: "debe" };
  return { texto: "—", estado: "al_dia" };
}

export const ETIQUETA_TIPO_MOVIMIENTO: Record<TipoMovimiento, string> = { aporte: "Aporte", gasto: "Gasto", terreno: "Terreno", adjudicacion: "Adjudicación", participacion: "Participación" };

/** Link del movimiento a su origen: el gasto, o la compra del terreno para sus pagos; aportes y ajustes no llevan. */
export function linkMovimiento(m: Movimiento): string | null {
  if (m.tipo === "terreno") return RUTA_COMPRA_TERRENO;
  return m.tipo === "gasto" && m.gasto_id !== null ? `/gastos/${m.gasto_id}` : null;
}

/** Título de la tarjeta Saldo del detalle (Mati, 2026-11-02): "A favor de Francisco", "Francisco debe" o "Francisco está al día". */
export function tituloSaldo(c: Cuenta): string {
  const estado = estadoSaldo(c.saldo_usd);
  return estado === "a_favor" ? `A favor de ${c.nombre}` : estado === "debe" ? `${c.nombre} debe` : `${c.nombre} está al día`;
}

/** Detalle de la tarjeta Saldo: lo atrasado de los cambios de participación (adjudicaciones incluidas), si hay. */
export function detalleSaldo(c: Cuenta): string | undefined {
  if (parseMonto(sinSigno(c.ajustes_usd)) === null) return undefined;
  const sentido = c.ajustes_usd.trim().startsWith("-") ? "en contra" : "a favor";
  return `Incluye ${formatSaldo(c.ajustes_usd)} ${sentido} por cambios de participación`;
}

/** Los movimientos con lo más nuevo arriba (Mati, 2026-11-02); el backend los da de lo más viejo a lo más nuevo. */
export const recientesPrimero = (movimientos: Movimiento[]): Movimiento[] => [...movimientos].reverse();

/** Texto de la tarjeta "Estado de cuenta corriente" (Inicio) y del saldo en Mi cuenta: el saldo de gastos, con lo vencido de los llamados. */
export function resumenCuenta(c: Cuenta): { valor: string; detalle: string } {
  const vencido = parseMonto(c.vencido_usd) !== null ? ` · vencido ${formatMoneda(c.vencido_usd, "USD")}` : "";
  return { valor: formatSaldo(c.saldo_usd), detalle: `${ETIQUETA_SALDO[estadoSaldo(c.saldo_usd)]}${vencido}` };
}

/** De dónde sale el saldo, debajo del "A favor" o "Debe" del fiduciante (Mati, 2026-11-04: al lado de los gastos de la
 * obra no se entendía): aportado − su parte de lo pagado ± lo atrasado de los cambios de participación. */
export function cuentaDelSaldo(c: Cuenta): string {
  const estado = ETIQUETA_SALDO[estadoSaldo(c.saldo_usd)];
  const hayAjustes = parseMonto(sinSigno(c.ajustes_usd)) !== null;
  if (parseMonto(c.gastos_usd) === null && !hayAjustes) return `${estado} · todavía no se pagó ningún gasto`;
  const ajustes = hayAjustes ? ` ${c.ajustes_usd.trim().startsWith("-") ? "−" : "+"} ${formatSaldo(c.ajustes_usd)} por cambios de participación` : "";
  return `${estado} · aportaste ${formatMoneda(c.aportado_usd, "USD")} − tu parte de lo pagado ${formatMoneda(c.gastos_usd, "USD")}${ajustes}`;
}

/** Aviso de gastos que no se le cargaron a nadie (sin distribución completa vigente a su fecha). */
export function avisoSinRepartir(t: TotalesCuentas): string | null {
  if (parseMonto(t.sin_repartir_usd) === null) return null;
  return `Hay ${formatMoneda(t.sin_repartir_usd, "USD")} de gastos sin repartir: no había una distribución completa vigente a su fecha. Revisá la participación.`;
}

// ------------------------------------------------------------------ tarjeta Previsión (Mati, 2026-10-18)

/** ¿Hay algo para la tarjeta Previsión? Un desarrollo sin previsiones, aportes ni gastos no muestra ceros. */
export const hayPrevision = (t: TotalesCuentas): boolean =>
  [t.pedido_usd, t.aportado_usd, t.gastos_usd].some((m) => parseMonto(sinSigno(m)) !== null);

/** La plata adelantada que queda: cobrado − gastado (= Σ saldos; las adjudicaciones se netean en cero). */
export function tarjetaPrevision(t: TotalesCuentas): { valor: string; tono: BadgeTono | undefined; detalle: string } {
  const estado = estadoSaldo(t.saldo_usd);
  return {
    valor: estado === "debe" ? saldoContable(t.saldo_usd) : formatSaldo(t.saldo_usd),
    tono: estado === "al_dia" ? undefined : TONO_SALDO[estado],
    detalle: `Pedido ${formatSaldo(t.pedido_usd)} · cobrado ${formatSaldo(t.aportado_usd)} · gastado ${formatSaldo(t.gastos_usd)} · falta cobrar ${formatSaldo(t.pendiente_usd)}`,
  };
}

// ------------------------------------------------------------------ tarjeta Falta aportar (Mati, 2026-10-20)

/**
 * Lo que le falta aportar al fiduciante de las previsiones emitidas (Inicio). null si no debe nada de los llamados.
 * Si hay algo vencido lo dice en rojo; si no, cuándo vence la próxima.
 */
export function tarjetaFaltaAportar(c: CuentaMia): { valor: string; tono: BadgeTono; detalle: string } | null {
  if (parseMonto(sinSigno(c.pendiente_usd)) === null) return null;
  const valor = formatSaldo(c.pendiente_usd);
  if (parseMonto(sinSigno(c.vencido_usd)) !== null) return { valor, tono: "peligro", detalle: `Vencido ${formatSaldo(c.vencido_usd)} · ver mi cuenta` };
  const proxima = c.llamados
    .filter((l) => !l.vencida && parseMonto(sinSigno(l.pendiente_usd)) !== null)
    .map((l) => l.vencimiento)
    .sort()[0];
  return { valor, tono: "alerta", detalle: proxima ? `Vence el ${formatFecha(proxima)} · ver mi cuenta` : "Ver mi cuenta" };
}

// ------------------------------------------------------------------ vista con colores (Mati, 2026-10-18)

const aCentavosConSigno = (monto: string): bigint => {
  const t = monto.trim();
  const negativo = t.startsWith("-");
  const [ent = "0", dec = ""] = t.replace(/^-/, "").split(".");
  const c = BigInt(ent || "0") * 100n + BigInt((dec + "00").slice(0, 2));
  return negativo ? -c : c;
};
const deCentavosSinSigno = (c: bigint): string => {
  const a = c < 0n ? -c : c;
  return `${a / 100n}.${String(a % 100n).padStart(2, "0")}`;
};

/** Como en contabilidad (Mati, 2026-11-02): "USD 40.000,00" a favor (sin signo), "(USD 500,00)" debe. */
export function saldoContable(saldo: string): string {
  const monto = formatSaldo(saldo);
  return estadoSaldo(saldo) === "debe" ? `(${monto})` : monto;
}

/** Largo de la barra del saldo, de −100 (el que más debe… o más tiene) a 100, relativo al saldo más grande en valor
 * absoluto de la lista. Entero, sin float: es sólo para dibujar. */
export function proporcionSaldo(saldo: string, todos: string[]): number {
  const mayor = todos.reduce((m, s) => {
    const c = aCentavosConSigno(s);
    const abs = c < 0n ? -c : c;
    return abs > m ? abs : m;
  }, 0n);
  if (mayor === 0n) return 0;
  return Number((aCentavosConSigno(saldo) * 100n) / mayor);
}

export interface ResumenSaldos {
  a_favor_usd: string; // suma de los saldos positivos
  deben_usd: string; // suma de los negativos, sin signo
  cuantos_a_favor: number;
  cuantos_deben: number;
  cuantos_al_dia: number;
}

/** Totales para las tarjetas de arriba: cuánto tienen a favor los que tienen, cuánto deben los que deben. */
export function resumenSaldos(cuentas: { saldo_usd: string }[]): ResumenSaldos {
  let aFavor = 0n;
  let deben = 0n;
  const r = { cuantos_a_favor: 0, cuantos_deben: 0, cuantos_al_dia: 0 };
  for (const c of cuentas) {
    const s = aCentavosConSigno(c.saldo_usd);
    if (s > 0n) {
      aFavor += s;
      r.cuantos_a_favor += 1;
    } else if (s < 0n) {
      deben += s;
      r.cuantos_deben += 1;
    } else r.cuantos_al_dia += 1;
  }
  return { a_favor_usd: deCentavosSinSigno(aFavor), deben_usd: deCentavosSinSigno(deben), ...r };
}

/** "1 fiduciante" · "3 fiduciantes" · "Nadie". */
export const cuantosFiduciantes = (n: number): string => (n === 0 ? "Nadie" : n === 1 ? "1 fiduciante" : `${n} fiduciantes`);

// ------------------------------------------------------------------ filtros de los movimientos (Mati, 2026-11-02)

/** Tipo de movimiento para el filtro: los gastos incluyen la compra del terreno; los ajustes, adjudicaciones y cambios de participación. */
export type TipoFiltroMovimiento = typeof TODOS | "aporte" | "gasto" | "ajuste";
export interface FiltrosMovimientos {
  mes: string; // "2026-10" o TODOS
  tipo: TipoFiltroMovimiento;
}

const GRUPO_TIPO: Record<TipoMovimiento, Exclude<TipoFiltroMovimiento, typeof TODOS>> = {
  aporte: "aporte", gasto: "gasto", terreno: "gasto", adjudicacion: "ajuste", participacion: "ajuste",
};
export const OPCIONES_TIPO_MOVIMIENTO: OpcionSelect[] = [
  { valor: TODOS, etiqueta: "Todos los movimientos" },
  { valor: "aporte", etiqueta: "Aportes" },
  { valor: "gasto", etiqueta: "Gastos" },
  { valor: "ajuste", etiqueta: "Ajustes" },
];

/** De a cuántas filas se muestran; "Cargar más" suma otras tantas. */
export const PAGINA_MOVIMIENTOS = 20;

const mesDe = (fecha: string): string => fecha.slice(0, 7);

function sumarMes(mes: string, n: number): string {
  const [anio, m] = mes.split("-").map(Number) as [number, number];
  const total = anio * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, "0")}`;
}

/** El primer y el último mes con movimientos (null sin movimientos). */
function rangoMeses(movs: Movimiento[]): [string, string] | null {
  if (movs.length === 0) return null;
  const meses = movs.map((m) => mesDe(m.fecha)).sort();
  return [meses[0]!, meses[meses.length - 1]!];
}

/** Por defecto, el último mes con movimientos (para que la lista no sea infinita) y todos los tipos. */
export function filtrosMovimientosIniciales(movs: Movimiento[]): FiltrosMovimientos {
  return { mes: rangoMeses(movs)?.[1] ?? TODOS, tipo: TODOS };
}

export function filtrarMovimientos(movs: Movimiento[], f: FiltrosMovimientos): Movimiento[] {
  return recientesPrimero(movs).filter((m) => (f.mes === TODOS || mesDe(m.fecha) === f.mes) && (f.tipo === TODOS || GRUPO_TIPO[m.tipo] === f.tipo));
}

/** Las flechas ‹ › van al mes calendario anterior o siguiente, sin salir del rango con movimientos. */
export function navegacionMes(movs: Movimiento[], mes: string): { anterior: string | null; siguiente: string | null } {
  const rango = rangoMeses(movs);
  if (rango === null || mes === TODOS) return { anterior: null, siguiente: null };
  return { anterior: mes > rango[0] ? sumarMes(mes, -1) : null, siguiente: mes < rango[1] ? sumarMes(mes, 1) : null };
}

/** Todos los meses calendario del rango (también los sin movimientos), el más nuevo primero. */
export function opcionesMesMovimientos(movs: Movimiento[]): OpcionSelect[] {
  const rango = rangoMeses(movs);
  const meses: OpcionSelect[] = [];
  if (rango) for (let mes = rango[1]; mes >= rango[0]; mes = sumarMes(mes, -1)) meses.push({ valor: mes, etiqueta: nombreMes(mes) });
  return [{ valor: TODOS, etiqueta: "Todos los meses" }, ...meses];
}

export function textoCargarMas(total: number, visibles: number): string | null {
  return total > visibles ? `Cargar más (quedan ${total - visibles})` : null;
}
